import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Loader2, Paperclip, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatApiError } from '@/lib/formatApiError';
import {
  ATTACH_ACCEPT,
  MAX_PRODUCTION_ATTACHMENTS,
  deleteProductionAttachment,
  pollAttachmentUntilDone,
  retryAttachment,
  uploadProductionAttachment,
} from '@/lib/productionAttachments';
import { styleNoteFromAnalysis } from '@/lib/visualStyleNotes';
import { AttachPopover } from '@/components/productionAttach/AttachPopover';
import { AttachmentChips } from '@/components/productionAttach/AttachmentChips';
import { VoiceRecordPanel } from '@/components/productionAttach/VoiceRecordPanel';
import { CreatePlanChatStudio } from '@/components/createPlan/CreatePlanChatStudio';
import { CreateAgentPromptRecommender } from '@/components/CreateAgentPromptRecommender';
import { CREATE_AGENT_PARAM, CREATE_FLOW_PARAM, fetchCreationConfig } from '@/lib/creationPlan';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const ATTACH_TYPES = ['audio', 'video', 'image', 'file'];

function putFileWithProgress(url, file, contentType, onProgress) {
  const xhr = new XMLHttpRequest();
  const promise = new Promise((resolve, reject) => {
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || !event.total) return;
      onProgress(Math.min(99, Math.round((event.loaded / event.total) * 100)));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.onabort = () => reject(Object.assign(new Error('Upload cancelled'), { cancelled: true }));
    xhr.send(file);
  });
  return { promise, abort: () => xhr.abort() };
}

/**
 * Dashboard Create Story:
 * ATTACH → DESCRIBE → CREATE AGENT (flow + JSON plan + chat Q&A) → EXECUTE FLOW
 */
export function DashboardCreateStoryHero({
  onTaskCreated,
  recreateRequest = null,
  onRecreateConsumed,
  children = null,
} = {}) {
  const navigate = useNavigate();
  const [intakeText, setIntakeText] = useState('');
  const [preferredFlowId, setPreferredFlowId] = useState('');
  const [unifiedOnly, setUnifiedOnly] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [recordingOpen, setRecordingOpen] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [publishUploadBusy, setPublishUploadBusy] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishProgress, setPublishProgress] = useState(0);
  const [publishFileName, setPublishFileName] = useState('');
  const [publishPhase, setPublishPhase] = useState('idle');
  const [publishError, setPublishError] = useState('');
  const publishAbortRef = useRef(null);
  const publishCancelRef = useRef(false);
  const publishVideoInputRef = useRef(null);
  const [chatStudioOpen, setChatStudioOpen] = useState(false);
  const [chatSeed, setChatSeed] = useState(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const text = String(params.get('create') || '').trim().slice(0, 4000);
    if (!text) return null;
    return { text, preferredFlowId: String(params.get('flow') || '') };
  });
  const [searchParams] = useSearchParams();
  useEffect(() => {
    let cancelled = false;
    fetchCreationConfig()
      .then((config) => {
        if (!cancelled) setUnifiedOnly(config.unifiedOnly);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const createFlowId = (flowId) => (unifiedOnly ? 'unified_story' : (flowId || ''));

  // ── Detected visual style from analyzed image attachments ─────────────────
  const [heroStyleOverride, setHeroStyleOverride] = useState(null); // null = use auto, '' = cleared by user
  const [heroStyleEditing, setHeroStyleEditing] = useState(false);
  const [heroStyleDraft, setHeroStyleDraft] = useState('');

  const autoDetectedStyle = useMemo(() => {
    const parts = attachments
      .filter((a) => a.type === 'image' && a.processing_status === 'analyzed')
      .map((a) => styleNoteFromAnalysis(a.analysis))
      .filter(Boolean);
    return parts.join(' ').slice(0, 800);
  }, [attachments]);

  // Reset override when user removes all image attachments
  useEffect(() => {
    const hasImages = attachments.some((a) => a.type === 'image');
    if (!hasImages) { setHeroStyleOverride(null); setHeroStyleEditing(false); }
  }, [attachments]);

  const effectiveStyleNote = heroStyleOverride !== null ? heroStyleOverride : autoDetectedStyle;
  const agentParam = searchParams.get(CREATE_AGENT_PARAM) || '';
  const flowParam = searchParams.get(CREATE_FLOW_PARAM) || '';
  const createParam = searchParams.get('create') || '';

  const heroCardRef = useRef(null);
  const attachBtnRef = useRef(null);
  const pollTimersRef = useRef({});
  const uploadFilesRef = useRef(null);
  const attachInputBaseId = useId().replace(/:/g, '');
  const attachInputIds = {
    audio: `dash-attach-${attachInputBaseId}-audio`,
    video: `dash-attach-${attachInputBaseId}-video`,
    image: `dash-attach-${attachInputBaseId}-image`,
    file: `dash-attach-${attachInputBaseId}-file`,
  };

  useEffect(() => {
    return () => {
      Object.values(pollTimersRef.current).forEach((t) => clearTimeout(t));
    };
  }, []);

  /** Gallery "Recreate" → full-page chat with the original work prompt. */
  useEffect(() => {
    const prompt = String(recreateRequest?.text || recreateRequest?.prompt || '').trim();
    if (!prompt) return undefined;
    setIntakeText(prompt);
    setChatSeed({
      text: prompt,
      preferredFlowId: createFlowId(recreateRequest?.flowId || ''),
      params: recreateRequest?.params && typeof recreateRequest.params === 'object'
        ? recreateRequest.params
        : null,
      fromRecreate: true,
    });
    setChatStudioOpen(true);
    onRecreateConsumed?.();
    return undefined;
  }, [recreateRequest, onRecreateConsumed]);

  useEffect(() => {
    const text = createParam.trim().slice(0, 4000);
    if (!text) return undefined;
    setIntakeText(text);
    setChatSeed({
      text,
      preferredFlowId: createFlowId(flowParam || ''),
    });
    setChatStudioOpen(true);
    heroCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const next = new URLSearchParams(searchParams);
    next.delete('create');
    const search = next.toString();
    navigate({ pathname: '/dashboard', search: search ? `?${search}` : '' }, { replace: true });
    return undefined;
  }, [createParam, flowParam, navigate, searchParams]);

  /** Dashboard /tasks links with ?agent=new&flow=<id> open the agent locked to that flow. */
  useEffect(() => {
    if (!agentParam) return undefined;
    if (flowParam || unifiedOnly) {
      const flowId = createFlowId(flowParam);
      setPreferredFlowId(flowId);
      setChatSeed((prev) => ({
        ...(prev || {}),
        text: prev?.text || '',
        preferredFlowId: flowId,
      }));
    }
    if (agentParam === 'new') {
      setChatStudioOpen(true);
    }
    return undefined;
  }, [agentParam, flowParam, unifiedOnly]);

  const anyProcessing = attachments.some((a) =>
    ['uploading', 'uploaded', 'processing'].includes(a.processing_status)
  );

  // True while at least one image attachment is still being analyzed — the Create button
  // should stay disabled until the "Analyzing visual style…" spinner resolves.
  const isAnalyzingImages = attachments.some(
    (a) =>
      a.type === 'image' &&
      ['uploading', 'uploaded', 'processing'].includes(a.processing_status)
  );

  useEffect(() => {
    if (!isAnalyzingImages) return undefined;
    const timer = setTimeout(() => {
      setAttachments((prev) =>
        prev.map((a) =>
          a.type === 'image' &&
          ['uploading', 'uploaded', 'processing'].includes(a.processing_status)
            ? {
                ...a,
                processing_status: 'analyzed',
                error:
                  a.error ||
                  'Style analysis timed out — you can still Create, or retry the image.',
              }
            : a
        )
      );
    }, 45000);
    return () => clearTimeout(timer);
  }, [isAnalyzingImages]);

  const upsertAttachment = (entry) => {
    setAttachments((prev) => {
      const idx = prev.findIndex((a) => a.id === entry.id || (entry.localId && a.localId === entry.localId));
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...entry };
        return next;
      }
      return [...prev, entry].slice(0, MAX_PRODUCTION_ATTACHMENTS);
    });
  };

  const startPolling = (attachmentId) => {
    if (!attachmentId || pollTimersRef.current[attachmentId]) return;
    const tick = async () => {
      try {
        const updated = await Promise.race([
          pollAttachmentUntilDone(attachmentId, {
            intervalMs: 1600,
            timeoutMs: 40000,
          }),
          new Promise((resolve) => setTimeout(() => resolve(null), 42000)),
        ]);
        const st = updated?.processing_status;
        const timedOut = !updated || !['analyzed', 'failed'].includes(st);
        upsertAttachment({
          id: updated?.id || attachmentId,
          type: updated?.type,
          filename: updated?.filename,
          size: updated?.size,
          mime_type: updated?.mime_type,
          // Keep images usable on timeout (Create drops `failed` attachments).
          processing_status: timedOut ? 'analyzed' : st,
          duration_seconds: updated?.duration_seconds,
          transcript: updated?.transcript,
          analysis: updated?.analysis,
          storage_url: updated?.storage_url,
          error: timedOut
            ? 'Style analysis timed out — you can still Create, or retry the image.'
            : updated?.error,
          source: updated?.source,
          project_id: updated?.project_id,
        });
      } catch {
        upsertAttachment({
          id: attachmentId,
          processing_status: 'failed',
          error: 'Could not finish analyzing this image.',
        });
      } finally {
        delete pollTimersRef.current[attachmentId];
      }
    };
    pollTimersRef.current[attachmentId] = setTimeout(tick, 400);
  };

  const uploadFiles = async (fileList, attachmentType) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setAttachOpen(false);
    const remaining = MAX_PRODUCTION_ATTACHMENTS - attachments.length;
    if (remaining <= 0) {
      toast.error(`You can attach up to ${MAX_PRODUCTION_ATTACHMENTS} files`);
      return;
    }
    const batch = files.slice(0, remaining);
    setUploadBusy(true);
    try {
      for (const file of batch) {
        const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        upsertAttachment({
          localId,
          id: localId,
          filename: file.name,
          size: file.size,
          type: attachmentType === 'file' ? 'document' : attachmentType,
          processing_status: 'uploading',
          source: 'attach',
          previewUrl: file.type?.startsWith('image/') ? URL.createObjectURL(file) : undefined,
        });
        try {
          const { attachment, projectId: pid } = await uploadProductionAttachment(file, {
            attachmentType,
            projectId,
            source: 'attach',
          });
          if (pid) setProjectId(pid);
          upsertAttachment({
            localId,
            id: attachment.id,
            type: attachment.type,
            filename: attachment.filename,
            size: attachment.size,
            mime_type: attachment.mime_type,
            processing_status: attachment.processing_status || 'uploaded',
            storage_url: attachment.storage_url,
            source: attachment.source || 'attach',
            project_id: attachment.project_id || pid,
            previewUrl: file.type?.startsWith('image/')
              ? URL.createObjectURL(file)
              : undefined,
          });
          startPolling(attachment.id);
          toast.success(`${file.name} uploaded`);
        } catch (err) {
          upsertAttachment({
            localId,
            id: localId,
            processing_status: 'failed',
            error: formatApiError(err, 'Upload failed'),
          });
          toast.error(formatApiError(err, `Failed: ${file.name}`));
        }
      }
    } finally {
      setUploadBusy(false);
    }
  };

  uploadFilesRef.current = uploadFiles;

  const onAttachInputChange = (attachmentType, event) => {
    // FileList is live — copy before clearing value or the list becomes empty
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    uploadFilesRef.current?.(files, attachmentType);
  };

  const handleRecordComplete = async (blob) => {
    setRecordingOpen(false);
    if (!blob) return;
    setUploadBusy(true);
    const localId = `rec-${Date.now()}`;
    upsertAttachment({
      localId,
      id: localId,
      filename: `voice-instruction-${Date.now()}.webm`,
      size: blob.size,
      type: 'audio',
      processing_status: 'uploading',
      source: 'record',
    });
    try {
      const { attachment, projectId: pid } = await uploadProductionAttachment(blob, {
        attachmentType: 'recording',
        projectId,
        source: 'record',
        filename: `voice-instruction-${Date.now()}.webm`,
      });
      if (pid) setProjectId(pid);
      upsertAttachment({
        localId,
        id: attachment.id,
        type: attachment.type,
        filename: attachment.filename,
        size: attachment.size,
        processing_status: attachment.processing_status || 'uploaded',
        storage_url: attachment.storage_url,
        source: 'record',
        project_id: attachment.project_id || pid,
      });
      startPolling(attachment.id);
      toast.message('Voice instruction uploaded — analyzing…');
    } catch (err) {
      upsertAttachment({
        localId,
        id: localId,
        processing_status: 'failed',
        error: formatApiError(err, 'Recording upload failed'),
      });
      toast.error(formatApiError(err, 'Recording upload failed'));
    } finally {
      setUploadBusy(false);
    }
  };

  const removeAttachment = async (att) => {
    const id = att.id;
    setAttachments((prev) => prev.filter((a) => a.id !== id && a.localId !== att.localId));
    if (id && !String(id).startsWith('local-') && !String(id).startsWith('rec-')) {
      try {
        await deleteProductionAttachment(id);
      } catch {
        /* ignore */
      }
    }
  };

  const handleRetry = async (att) => {
    if (!att?.id || String(att.id).startsWith('local')) return;
    try {
      upsertAttachment({ id: att.id, processing_status: 'uploaded', error: null });
      await retryAttachment(att.id);
      startPolling(att.id);
      toast.message('Retrying analysis…');
    } catch (err) {
      toast.error(formatApiError(err, 'Retry failed'));
    }
  };

  const handleUnderstand = ({ forceContinue = false } = {}) => {
    const text = (intakeText || '').trim();
    if (!text && !attachments.length) {
      toast.error('Write instructions, record a voice note, or attach reference material');
      return;
    }

    if (!forceContinue && anyProcessing) {
      toast.message('Still analyzing attachments — wait or continue without unfinished ones');
      return;
    }

    const usable = attachments.filter((a) => {
      const id = String(a.id || '');
      if (!id || id.startsWith('local') || id.startsWith('rec-')) return false;
      return forceContinue ? a.processing_status === 'analyzed' : a.processing_status !== 'failed';
    });
    const analyzed = usable.filter((a) => a.processing_status === 'analyzed');

    setChatSeed({
      text,
      preferredFlowId: createFlowId(preferredFlowId),
      transcript: analyzed.map((a) => a.transcript).filter(Boolean).join('\n\n'),
      extractedText: analyzed
        .map((a) => a.analysis?.extracted_text || a.extracted_text)
        .filter(Boolean)
        .join('\n\n'),
      attachmentIds: usable.map((a) => a.id),
      projectId: projectId || null,
      // Pass image attachment IDs separately so CreatePlanChatStudio can (re-)analyze style
      imageAttachmentIds: analyzed.filter((a) => a.type === 'image').map((a) => a.id),
      imageStyleNotes: effectiveStyleNote || analyzed
        .filter((a) => a.type === 'image')
        .map((a) => styleNoteFromAnalysis(a.analysis))
        .filter(Boolean)
        .join(' ')
        .slice(0, 800) || '',
      // Full attachment display objects so the chatbot can show thumbnails
      seedAttachments: usable.map((a) => ({
        id: a.id,
        type: a.type || 'document',
        filename: a.filename || '',
        storage_url: a.storage_url || '',
        processing_status: a.processing_status,
      })),
    });
    setChatStudioOpen(true);
  };

  const closePublishDialog = () => {
    publishCancelRef.current = true;
    publishAbortRef.current?.();
    publishAbortRef.current = null;
    setPublishUploadBusy(false);
    setPublishDialogOpen(false);
    setPublishPhase('idle');
    setPublishProgress(0);
    setPublishError('');
    setPublishFileName('');
  };

  const uploadVideoAndOpenPublish = async (file) => {
    if (!file) return;
    if (file.type && !file.type.startsWith('video/')) {
      setPublishPhase('error');
      setPublishError('Choose a video file');
      return;
    }
    const filename = file.name || `video-${Date.now()}.mp4`;
    const contentType = file.type || 'video/mp4';
    setPublishFileName(filename);
    setPublishError('');
    setPublishProgress(0);
    setPublishPhase('uploading');
    setPublishUploadBusy(true);
    publishCancelRef.current = false;
    try {
      const { data: presigned } = await api.post('/media/presigned-url', null, {
        params: { filename, content_type: contentType },
      });
      if (publishCancelRef.current) return;
      const uploadUrl = presigned.upload_url || presigned.presigned_url;
      const s3Key = presigned.s3_key;
      if (!uploadUrl || !s3Key) throw new Error('Could not get an upload URL');
      const upload = putFileWithProgress(uploadUrl, file, contentType, setPublishProgress);
      publishAbortRef.current = upload.abort;
      await upload.promise;
      publishAbortRef.current = null;
      if (publishCancelRef.current) return;
      setPublishPhase('finishing');
      setPublishProgress(100);
      let s3Url = String(presigned.view_url || presigned.object_url || presigned.s3_url || '').trim();
      try {
        const { data: registered } = await api.post('/media/register-s3-upload', {
          s3_key: s3Key,
          content_type: contentType,
        });
        s3Url = String(registered.s3_url || registered.object_url || s3Url).trim();
      } catch {
        /* The public object URL from the presign response is enough to prefill Publish. */
      }
      if (publishCancelRef.current) return;
      if (!s3Url) throw new Error('Upload finished without an S3 URL');
      setPublishPhase('done');
      navigate('/social-publish', { state: { videoUrl: s3Url } });
    } catch (err) {
      publishAbortRef.current = null;
      if (err?.cancelled) return;
      setPublishPhase('error');
      setPublishError(formatApiError(err, 'Could not upload the video'));
    } finally {
      setPublishUploadBusy(false);
    }
  };

  /** Fill intake only — user edits, then clicks Create. */
  const handlePromptRecommend = (row) => {
    const prompt = String(row?.prompt || '').trim();
    if (!prompt) return;
    setIntakeText(prompt);
    setPreferredFlowId(createFlowId(String(row?.flowId || '').trim()));
  };

  return (
    <>
      {typeof document !== 'undefined'
        ? createPortal(
            <>
              {ATTACH_TYPES.map((type) => (
                <input
                  key={type}
                  id={attachInputIds[type]}
                  data-attach-file-input={type}
                  data-testid={`attach-input-${type}`}
                  type="file"
                  multiple
                  accept={ATTACH_ACCEPT[type]}
                  disabled={uploadBusy || recordingOpen}
                  tabIndex={-1}
                  aria-hidden
                  style={{
                    position: 'fixed',
                    left: '-9999px',
                    top: 0,
                    width: 1,
                    height: 1,
                    opacity: 0,
                  }}
                  onChange={(e) => onAttachInputChange(type, e)}
                />
              ))}
            </>,
            document.body
          )
        : null}

      <motion.section
        layoutId="dashboard-create-story-shell"
        ref={heroCardRef}
        className="rounded-[20px] overflow-hidden relative border border-[#1f2635] w-full flex flex-col p-4 sm:p-6 group shadow-xl bg-[#12151d]"
        data-testid="dashboard-create-story-hero"
      >
        <img
          src="/LandingPageAssets/HeroSectionBG.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-[1.02] transition-transform duration-700 opacity-70"
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(to top, rgba(10, 13, 18, 0.96) 0%, rgba(10, 13, 18, 0.72) 42%, rgba(10, 13, 18, 0.25) 70%, transparent 100%), linear-gradient(to right, rgba(10, 13, 18, 0.75) 0%, transparent 55%)',
          }}
        />

        <div className="relative z-10 w-full max-w-none">
          <div className="rounded-2xl border border-white/15 bg-[#0b0f16]/88 backdrop-blur-xl p-3.5 sm:p-4 space-y-3 shadow-[0_12px_40px_rgba(0,0,0,0.45)] w-full">
            <AttachmentChips
              attachments={attachments}
              onRemove={removeAttachment}
              onRetry={handleRetry}
            />

            {/* ── Detected visual style banner ── */}
            {attachments.some((a) => a.type === 'image') ? (
              <div className="rounded-xl border border-[#2DD4BF]/25 bg-[#2DD4BF]/5 px-3 py-2.5">
                {autoDetectedStyle || effectiveStyleNote ? (
                  heroStyleEditing ? (
                    /* Edit mode */
                    <div className="space-y-2">
                      <p className="text-[10px] font-semibold text-[#2DD4BF] flex items-center gap-1.5">
                        <Sparkles className="w-3 h-3" /> Edit detected visual style
                      </p>
                      <textarea
                        autoFocus
                        rows={3}
                        value={heroStyleDraft}
                        onChange={(e) => setHeroStyleDraft(e.target.value)}
                        className="w-full bg-white/[0.04] border border-white/10 focus:border-[#2DD4BF]/60 rounded-lg px-2.5 py-1.5 text-xs text-white placeholder:text-[#4B5563] focus:outline-none resize-none"
                        placeholder="Describe the visual style to apply to all scenes…"
                      />
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setHeroStyleOverride(heroStyleDraft.trim() || null);
                            setHeroStyleEditing(false);
                          }}
                          className="px-3 py-1 rounded-full text-[11px] font-semibold bg-[#2DD4BF] text-[#06080D] flex items-center gap-1 cursor-pointer"
                        >
                          <Check className="w-3 h-3" /> Save
                        </button>
                        <button
                          type="button"
                          onClick={() => setHeroStyleEditing(false)}
                          className="px-3 py-1 rounded-full text-[11px] text-[#9CA3AF] hover:text-white cursor-pointer"
                        >
                          Cancel
                        </button>
                        {heroStyleOverride !== null && (
                          <button
                            type="button"
                            onClick={() => { setHeroStyleOverride(null); setHeroStyleEditing(false); }}
                            className="text-[10px] text-[#6B7280] hover:text-[#f87171] cursor-pointer ml-auto"
                          >
                            Reset to auto-detected
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* View mode */
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-[#2DD4BF] mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-semibold text-[#2DD4BF] mb-0.5">
                          Detected visual style {heroStyleOverride !== null ? '(edited)' : ''}
                        </p>
                        <p className="text-[11px] text-[#cbd5e1] leading-relaxed line-clamp-3">
                          {effectiveStyleNote}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 ml-1">
                        <button
                          type="button"
                          onClick={() => {
                            setHeroStyleDraft(effectiveStyleNote);
                            setHeroStyleEditing(true);
                          }}
                          className="text-[10px] text-[#2DD4BF]/70 hover:text-[#2DD4BF] underline-offset-2 hover:underline cursor-pointer"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setHeroStyleOverride('')}
                          className="text-[#6B7280] hover:text-[#f87171] cursor-pointer"
                          title="Clear style note"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  )
                ) : isAnalyzingImages ? (
                  <div className="flex items-center gap-2 text-[11px] text-[#9CA3AF]">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-[#2DD4BF]" />
                    Analyzing visual style from your reference images…
                  </div>
                ) : (
                  <div className="text-[11px] text-[#9CA3AF]">
                    Style analysis didn’t finish. You can still Create — images will be used as content references.
                  </div>
                )}
              </div>
            ) : null}

            {recordingOpen ? (
              <VoiceRecordPanel
                open={recordingOpen}
                onCancel={() => setRecordingOpen(false)}
                onComplete={handleRecordComplete}
              />
            ) : null}

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)] gap-4 items-stretch">
              <div className="flex flex-col min-w-0 gap-3">
                <CreateAgentPromptRecommender
                  onSelect={handlePromptRecommend}
                  className="w-full"
                />
                <div className="rounded-2xl border border-white/20 bg-[#151b26] shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] focus-within:border-[#2DD4BF]/55 focus-within:ring-1 focus-within:ring-[#2DD4BF]/25">
                  <textarea
                  rows={6}
                  value={intakeText}
                  onChange={(e) => setIntakeText(e.target.value)}
                  placeholder="e.g. Create a cinematic 3-minute product story using the attached material…"
                  className="w-full flex-1 bg-transparent border-none text-white text-base sm:text-sm placeholder:text-[#A1AEC0] focus:outline-none resize-none font-body leading-relaxed min-h-[128px] sm:min-h-[186px] px-3.5 pt-3"
                    data-testid="dashboard-create-intake"
                  />
                  {anyProcessing && (
                  <div className="flex items-start gap-2 text-[11px] text-[#CBD5E1] px-3.5 pb-2">
                    <Loader2 className="w-3.5 h-3.5 text-[#2DD4BF] animate-spin shrink-0 mt-0.5" />
                    <span className="leading-relaxed">Analyzing attachments into structured asset intelligence…</span>
                  </div>
                )}
                <div className="flex items-center gap-2 px-3 pb-3 pt-2 border-t border-white/15 bg-white/[0.03] rounded-b-2xl flex-wrap sm:flex-nowrap">
                  <button
                    ref={attachBtnRef}
                    type="button"
                    disabled={uploadBusy || recordingOpen || attachments.length >= MAX_PRODUCTION_ATTACHMENTS}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setAttachOpen((o) => !o);
                    }}
                    aria-haspopup="menu"
                    aria-expanded={attachOpen}
                    className="shrink-0 px-2.5 py-1.5 rounded-full text-[10px] font-semibold flex items-center gap-1.5 cursor-pointer transition-all border border-white/25 bg-white/[0.08] text-[#E5E7EB] hover:text-white hover:bg-white/[0.14] disabled:opacity-50"
                    data-testid="attach-button"
                  >
                    {uploadBusy ? (
                      <Loader2 className="w-3 h-3 animate-spin text-[#2DD4BF]" />
                    ) : (
                      <Paperclip className="w-3 h-3" />
                    )}
                    {uploadBusy ? 'Uploading…' : 'Attach'}
                  </button>

                  <AttachPopover
                    open={attachOpen}
                    onClose={() => setAttachOpen(false)}
                    anchorRef={attachBtnRef}
                    disabled={uploadBusy || recordingOpen}
                    inputIds={attachInputIds}
                    onStartRecord={() => setRecordingOpen(true)}
                  />

                  <span className="hidden sm:block text-[11px] text-[#CBD5E1] leading-snug min-w-0 flex-1">
                    Attach source material and describe what to create — Twinnee picks the right flow, drafts a plan, asks for any missing details in chat, then runs it.
                  </span>
                  {attachments.length ? (
                    <span className="text-[10px] text-[#6B7280] shrink-0">
                      {attachments.length}/{MAX_PRODUCTION_ATTACHMENTS}
                    </span>
                  ) : null}

                  {anyProcessing && attachments.some((a) => a.processing_status === 'analyzed') && !isAnalyzingImages ? (
                    <button
                      type="button"
                      onClick={() => handleUnderstand({ forceContinue: true })}
                      className="text-[10px] text-[#2DD4BF] underline-offset-2 hover:underline cursor-pointer"
                    >
                      Continue without pending
                    </button>
                  ) : null}

                  <button
                    type="button"
                    onClick={() => handleUnderstand()}
                    disabled={uploadBusy || recordingOpen || isAnalyzingImages}
                    title={isAnalyzingImages ? 'Analyzing visual style from your reference images…' : undefined}
                    className="ml-auto sm:ml-0 shrink-0 px-5 py-2 rounded-full bg-gradient-to-r from-[#93C5FD] via-[#7DD3FC] to-[#5EEAD4] text-[#06080D] font-bold text-xs sm:text-sm tracking-wide hover:brightness-105 active:scale-[0.98] transition-all cursor-pointer disabled:opacity-60"
                  >
                    Create →
                  </button>
                </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-white/15" />
                  <span className="text-[11px] font-semibold tracking-[0.16em] text-[#9CA3AF]">OR</span>
                  <div className="h-px flex-1 bg-white/15" />
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl bg-gradient-to-r from-[#4a2a18] via-[#3d221c] to-[#2c1816] px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold tracking-wide text-white">Upload video</p>
                    <p className="text-[11px] text-[#e7cbb8] leading-relaxed mt-0.5">
                      Already have a video? Upload it, then add the title and details on Publish.
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={recordingOpen}
                    onClick={() => {
                      setPublishPhase('idle');
                      setPublishProgress(0);
                      setPublishError('');
                      setPublishFileName('');
                      publishVideoInputRef.current?.click();
                    }}
                    className="shrink-0 px-5 py-2 rounded-full bg-gradient-to-r from-[#FDE68A] via-[#FDBA74] to-[#FB7185] text-[#3b1208] font-bold text-xs sm:text-sm tracking-wide hover:brightness-105 active:scale-[0.98] transition-all cursor-pointer disabled:opacity-50"
                    data-testid="upload-publish-button"
                  >
                    Upload
                  </button>
                </div>
              </div>
              {children ? <div className="min-w-0 self-start">{children}</div> : null}
            </div>
          </div>
        </div>
      </motion.section>

      <input
        ref={publishVideoInputRef}
        type="file"
        accept="video/mp4,video/quicktime,video/webm,video/*"
        className="hidden"
        tabIndex={-1}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file) return;
          setPublishDialogOpen(true);
          uploadVideoAndOpenPublish(file);
        }}
      />
      <Dialog
        open={publishDialogOpen}
        onOpenChange={(open) => {
          if (!open) closePublishDialog();
          else setPublishDialogOpen(true);
        }}
      >
        <DialogContent className="border-white/10 bg-[#12151d] text-white sm:rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Upload & publish</DialogTitle>
            <DialogDescription className="text-[#9CA3AF]">
              The file uploads here. Title and description stay for you to fill in on Publish.
            </DialogDescription>
          </DialogHeader>
          {publishPhase === 'idle' ? (
            <button
              type="button"
              onClick={() => publishVideoInputRef.current?.click()}
              className="w-full rounded-2xl border border-dashed border-white/20 bg-white/[0.03] px-4 py-8 text-sm font-semibold text-white hover:bg-white/[0.06]"
            >
              Choose a video
            </button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-white truncate">{publishFileName}</p>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full bg-[#34d399] transition-[width] duration-200"
                  style={{ width: `${publishPhase === 'error' ? publishProgress : Math.max(publishProgress, publishPhase === 'finishing' || publishPhase === 'done' ? 100 : 4)}%` }}
                />
              </div>
              <p className="text-xs text-[#9CA3AF]">
                {publishPhase === 'error'
                  ? publishError
                  : publishPhase === 'finishing'
                    ? 'Finishing…'
                    : publishPhase === 'done'
                      ? 'Uploaded. Opening Publish…'
                      : `Uploading ${publishProgress}%`}
              </p>
              {publishPhase === 'error' ? (
                <button
                  type="button"
                  onClick={() => publishVideoInputRef.current?.click()}
                  className="rounded-full px-4 py-2 text-xs font-semibold bg-[#34d399] text-[#06211b]"
                >
                  Try another video
                </button>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CreatePlanChatStudio
        open={chatStudioOpen || Boolean(agentParam)}
        seed={chatSeed}
        resumePlanId={agentParam && agentParam !== 'new' ? agentParam : null}
        syncUrl
        navigate={navigate}
        onTaskCreated={onTaskCreated}
        onNewPlan={() => {
          // Drop prior seed (incl. imageStyleNotes) so a blank plan cannot re-show
          // the previous run's detected visual style.
          setChatSeed(null);
          setHeroStyleOverride(null);
          setHeroStyleEditing(false);
          setHeroStyleDraft('');
        }}
        onClose={() => setChatStudioOpen(false)}
      />
    </>
  );
}

export default DashboardCreateStoryHero;
